import React, { useMemo, useState, useEffect } from 'react';

import { format, parseISO } from 'date-fns';

import Barcode from 'react-jsbarcode';

import { Col, Row } from 'react-bootstrap';
import { IParcelas } from '../CartaoSaudeDetalhe';

import { ContainerCarne, InputFake, PageBreak, PalcoBarCode } from './styles';

import api from '../../services/api';
import { useToast } from '../../hooks/toast';
import { useClinica } from '../../hooks/clinica';

import {
  formataCPF,
  formataCNPJ,
  formatPrice,
  TIPO_PARCELA,
} from '../../utils/funcoes';
import Loading from '../../components/Loading';
import getResponseErrors from '../../utils/getResponseErrors';
import { useAuth } from '../../hooks/auth';

interface IPaciente {
  PacCodigo: number;
  PacNome: string;
  PacPessoaFisica: boolean;
  PacCPF: string;
  PacCNPJ: string;
  PacCPFFormatado: string;
  PacCNPJFormatado: string;
}

interface IDependente {
  DepCodigo: number;
  DepTitular: boolean;
  DepResponsavel: boolean;
  paciente: IPaciente;
}

interface IDadosCarne {
  PlaCodigo: number;
  PlaNumero: string;
  titularEResponsavel: IDependente[];
  qtdDependentes: number;
  parcelas: IParcelas[];
  juros: number;
  multa: number;
}

interface IComponentProps {
  PlaCodigo: number;
}

const Carne: React.FC<IComponentProps> = ({ PlaCodigo }) => {
  const { addToast } = useToast();
  const { imagensClinica } = useClinica();
  const { clinica } = useAuth();
  const [loading, setLoading] = useState(false);
  const [titular, setTitular] = useState<IPaciente>();
  const [responsavel, setResponsavel] = useState<IPaciente>();
  const [parcelas, setParcelas] = useState<IParcelas[]>();
  const [plaNumero, setPlaNumero] = useState<string>();
  const [qtdDependentes, setQtdDependentes] = useState(0);
  const [multaJuros, setMultaJuros] = useState({ multa: 0, juros: 0 });

  const logoCarne =
    imagensClinica.find(img => img.nome === 'logoCarne')?.arquivo_url || '';

  useEffect(() => {
    async function loadDados(): Promise<void> {
      setLoading(true);
      try {
        const response = await api.get<IDadosCarne>(
          `/planos/carne/${PlaCodigo}`,
        );

        const pacientesFormatados = response.data.titularEResponsavel.map(
          dep => {
            return {
              ...dep,
              paciente: {
                ...dep.paciente,
                PacCPFFormatado: formataCPF(dep.paciente.PacCPF),
                PacCNPJFormatado: formataCNPJ(dep.paciente.PacCNPJ),
              },
            };
          },
        );

        const ultimaParcela = Math.max(
          ...response.data.parcelas
            .filter(p => p.CprNParcela < 1000)
            .map(p => p.CprNParcela),
          12,
        );

        const parcelasFormatadas = response.data.parcelas.map(parc => {
          let CprHistorico = `PRESTAÇÃO: ${parc.CprNParcela}/${ultimaParcela}`;
          if (parc.CprNParcela === TIPO_PARCELA.INCLUSAO_DEPENDENTE)
            CprHistorico = 'INCLUSÃO';
          if (parc.CprNParcela === TIPO_PARCELA.MULTA_CARTAO)
            CprHistorico = 'MULTA';
          return {
            ...parc,
            CprDataVenc: parseISO(parc.CprDataVenc.toString()),
            CprDataVencFormatado: format(
              parseISO(parc.CprDataVenc.toString()),
              'dd/MM/yyyy',
            ),
            CprValorFormatado: formatPrice(parc.CprValor),
            CprHistorico,
          };
        });

        setPlaNumero(`Nº ${response.data.PlaNumero}`);

        setTitular(
          pacientesFormatados.find(dep => dep.DepTitular === true)?.paciente,
        );
        setResponsavel(
          pacientesFormatados.find(dep => dep.DepResponsavel === true)
            ?.paciente,
        );

        setQtdDependentes(response.data.qtdDependentes);

        setParcelas(parcelasFormatadas);

        setMultaJuros({
          multa: response.data.multa,
          juros: response.data.juros,
        });

        setTimeout(() => {
          window.print();
        }, 2000);
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          title: 'Não foi possível carregar dados do carnê',
          type: 'error',
          description: mensagemErro,
        });
      } finally {
        setLoading(false);
      }
    }
    loadDados();
  }, [PlaCodigo, addToast]);

  const documentoExibir = useMemo(() => {
    if (titular?.PacPessoaFisica) {
      if (responsavel) {
        return `CPF DO RESPONSÁVEL: ${responsavel?.PacCPFFormatado}`;
      }
      return `CPF DO TITULAR: ${titular?.PacCPFFormatado}`;
    }
    return `CNPJ DO RESPONSÁVEL: ${responsavel?.PacCNPJFormatado}`;
  }, [responsavel, titular]);

  return (
    <ContainerCarne>
      {loading && <Loading />}
      {parcelas && parcelas.length > 0 ? (
        <>
          {parcelas.map((parc, index) => (
            <>
              <div
                className="border border-dark"
                style={{
                  width: '50%',
                  float: 'left',
                }}
                key={parc.CprCodigo.toString()}
              >
                <div className="d-block border-bottom border-dark py-5 px-2">
                  <div className="d-flex justify-content-between align-items-center mt-2 mb-5">
                    <img
                      src={logoCarne}
                      alt="Carne"
                      style={{ width: '136px', marginRight: '8px' }}
                    />
                    <b>{plaNumero}</b>
                    <InputFake>{parc.CprHistorico}</InputFake>
                    <InputFake>{`VALOR: ${parc.CprValorFormatado}`}</InputFake>
                  </div>
                  <InputFake style={{ width: '100%' }}>
                    {titular && <>TITULAR: {titular.PacNome}</>}
                    {responsavel && (
                      <>
                        <b> RESPONSÁVEL: </b>
                        {responsavel.PacNome}
                      </>
                    )}
                  </InputFake>
                </div>
                <div className="d-block py-1 px-2">
                  <div className="d-flex justify-content-between">
                    <div className="mt-2">
                      <div className="d-block">
                        <div className="d-flex">
                          <div>
                            <img
                              src={logoCarne}
                              alt="Carne"
                              style={{ width: '136px' }}
                            />
                          </div>
                          <div className="pl-2">
                            <b className="d-block mb-1">{plaNumero}</b>

                            <InputFake>{`VENC.: ${parc.CprDataVencFormatado}`}</InputFake>
                          </div>
                        </div>
                      </div>
                      <div className="d-block">
                        <div className="d-flex mt-3">
                          {multaJuros.multa > 0 &&
                            `Após o vencimento aplicar multa de ${multaJuros.multa}% e juros de ${multaJuros.juros}% ao mês.`}
                        </div>
                      </div>
                    </div>
                    {parc.qrcodebase64 && clinica.ConfigBoleto?.Emite && (
                      <>
                        <b className="pt-1">PIX</b>

                        <img
                          src={`data:image/jpeg;base64,${parc.qrcodebase64}`}
                          alt={parc.CprCodigo.toString()}
                          style={{ width: '107px' }}
                        />
                      </>
                    )}
                  </div>
                  <div className="d-flex justify-content-between align-items-center my-2">
                    <InputFake>{`DEPENDENTES: ${qtdDependentes}`}</InputFake>
                    <InputFake>{parc.CprHistorico}</InputFake>
                    <InputFake>{`VALOR: ${parc.CprValorFormatado}`}</InputFake>
                  </div>
                  <InputFake style={{ width: '100%' }} className="my-2">
                    {titular && <>TITULAR: {titular.PacNome}</>}
                    {responsavel && (
                      <>
                        <b> RESPONSÁVEL: </b>
                        {responsavel.PacNome}
                      </>
                    )}
                  </InputFake>
                  <InputFake style={{ width: '100%' }} className="my-2">
                    {documentoExibir}
                  </InputFake>
                  <PalcoBarCode>
                    {parc.codigoBarras && clinica.ConfigBoleto?.Emite && (
                      <Barcode
                        value={parc.codigoBarras}
                        options={{
                          format: 'ITF',
                          height: 50,
                          text: parc.linhaDigitavel,
                        }}
                      />
                    )}
                  </PalcoBarCode>
                </div>
              </div>
              {(index === 3 ||
                index === 7 ||
                (index === 11 && parcelas.length > 12) ||
                (index === 15 && parcelas.length > 12) ||
                (index === 19 && parcelas.length > 12) ||
                (index === 23 && parcelas.length > 12)) && (
                <PageBreak>&nbsp;</PageBreak>
              )}
            </>
          ))}
        </>
      ) : (
        <h4>{loading ? 'Buscando parcelas' : 'Nenhuma parcela em aberto'}</h4>
      )}
    </ContainerCarne>
  );
};

export default Carne;
